import React, { useState, useEffect } from "react";
import { Grid as GridLoading } from "react-loader-spinner";
import html2canvas from "html2canvas";
import SlotCounter from "react-slot-counter";
import { TypeAnimation } from "react-type-animation";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "../assets/styles/ops/Operations.scss";
import { styled } from "@mui/material/styles";

// Import modular components
import CollapsibleTable from "./ops/CollapsibleTable";
import FadeIn from "./compliance/FadeIn";
import OpsChart from "./ops/OpsChart";
import Search from "./ops/Search";

// Import MUI elements
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Skeleton from "@mui/material/Skeleton";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { Grid, Paper } from "@mui/material";

// Import framework to manage datepicker
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Import icons
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SendIcon from "@mui/icons-material/Send";

const Item = styled(Paper)(({ theme }) => ({
  padding: 10,
}));

const BoxContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

const ModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 850,
  height: 600,
  bgcolor: "background.paper",
  border: "1px solid #000",
  p: 4,
  overflow: "scroll",
  display: "block",
};

const ImageStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxHeight: 800,
  bgcolor: "background.paper",
  border: "1px solid #000",
  p: 4,
  overflow: "scroll",
  display: "block",
  padding: 0,
};

function parsePercentageString(percentageString: string) {
  const regex = /(\d+(\.\d+)?)/;

  const matches = percentageString.match(regex);

  if (matches && matches.length > 1) {
    return Math.round(parseFloat(matches[1]) * 10) / 10;
  } else {
    return NaN;
  }
}

function OpsManager({ parentToChild }: { parentToChild: any }) {
  const { mode } = parentToChild;

  // Set the default call center
  const [selectedCallCenter, setSelectedCallCenter] = useState<string>("CC001");
  const [callCenterText, setCallCenterText] = useState<string>("Davis-Morgan");

  // Default value is Total Call Volume
  const [selectedRadio, setSelectedRadio] = useState<string>("resolution_rate");
  const [totalCallData, setTotalCall] = useState<any>([]);
  const [aveHandleData, setAveHandleTime] = useState<any>([]);
  const [resolRateData, setResolRate] = useState<any>([]);
  const [aveWaitData, setAveWaittime] = useState<any>([]);
  const [totalUnresolData, setTotalUnresolved] = useState<any>([]);
  const [aveSentimentData, setAveSentiment] = useState<any>([]);

  const [numbersAsInt, setNumbersAsInt] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [alertData, setAlertData] = useState<any>();
  const [alertText, setAlertText] = useState<string[]>([]);

  const [isHighlightLoading, setIsHighlightLoading] = useState<boolean>(true);
  const [highlightData, setHighlightData] = useState<any[]>([]);
  const [highlightText, setHighlightText] = useState<string[]>([]);

  // Add state for selected option and start date
  const [selectedOption, setSelectedOption] = useState<string>("Last 7 days");
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(dayjs("2024-01-31"));
  const [startDate, setStartDate] = useState<dayjs.Dayjs>(dayjs("2024-01-25"));

  // Variables for sending a report
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [comments, setComments] = useState<string>("");
  const [frequency, setFrequency] = useState<string>("Daily");
  const [recurrences, setRecurrences] = useState<string>("1");
  const [rangeStartDate, setRangeStartDate] = useState<dayjs.Dayjs>(dayjs());
  const [rangeEndDate, setRangeEndDate] = useState<dayjs.Dayjs>(dayjs());
  const Today = dayjs();

  useEffect(() => {
    if (
      rangeStartDate &&
      rangeEndDate &&
      rangeStartDate.isAfter(rangeEndDate, "day")
    ) {
      setRangeStartDate(rangeEndDate);
    }
  }, [rangeStartDate, rangeEndDate]);

  // Set screenshot data for send report button
  const [screenshot, setScreenshot] = useState<any>();
  const [isImageOpen, setIsImageOpen] = useState<boolean>(false);

  const onButtonClick = async () => {
    const canvas = await html2canvas(document.body);
    const dataUrl = canvas.toDataURL("image/png");

    setScreenshot(dataUrl);
  };

  // Handle click for call center dropbown filter
  const handleCallCenterChange = (
    newSelection: string,
    selectedCallCenter: string
  ) => {
    if (newSelection !== selectedCallCenter) {
      setSelectedCallCenter(newSelection);
      setClickedLabel(null);

      if (newSelection === "CC001") {
        setCallCenterText("Davis-Morgan Center");
      } else if (newSelection === "CC002") {
        setCallCenterText("Dixon, Johnson and Dickerson Center");
      } else if (newSelection === "CC003") {
        setCallCenterText("Davis-Ross Center");
      }
    }
  };

  // Handle click for alerts
  const [clickedLabel, setClickedLabel] = useState(null);
  const handleLabelClick = (labelIndex: any) => {
    if (labelIndex === 0) {
      setSelectedCallCenter(alertData.key_alerts[0].cc_id);
      setSelectedRadio(alertData.key_alerts[0].key_metric[0]);
    } else if (labelIndex === 1) {
      setSelectedCallCenter(alertData.key_alerts[1].cc_id);
      setSelectedRadio(alertData.key_alerts[1].key_metric[0]);
    }
    setClickedLabel(labelIndex);
  };

  // Get useState for callreason selection
  const [selectedReasonFromChild, setSelectedReasonFromChild] =
    useState<string>("");
  const handleSelectedReasonChange = (label: string) => {
    setSelectedReasonFromChild(label);
  };

  const [selectedSubReasonFromChild, setSelectedSubReasonFromChild] =
    useState<string>("");
  const handleSelectedSubReasonChange = (label: string) => {
    setSelectedSubReasonFromChild(label);
  };

  // This state is passed to child components when everything is being reset
  const [isBeingResetTable, setIsBeingResetTable] = useState(false);
  const [isBeingResetChart, setIsBeingResetChart] = useState(false);
  const changeResetTable = () => {
    setIsBeingResetTable(false);
  };

  const changeResetChart = () => {
    setIsBeingResetChart(false);
  };

  // Updated DatePicker onChange handler
  const handleEndDateChange = (
    selectedOption: string,
    newDate: dayjs.Dayjs
  ) => {
    // Calculate updatedStartDate asynchronously
    setEndDate(newDate);
    let calculatedStartDate;

    setClickedLabel(null);

    const calculateUpdatedStartDate = () => {
      calculatedStartDate = newDate;

      if (selectedOption === "Last 7 days" && newDate instanceof dayjs) {
        calculatedStartDate = newDate.subtract(6, "days");
      } else if (
        selectedOption === "Last 30 days" &&
        newDate instanceof dayjs
      ) {
        calculatedStartDate = newDate.subtract(30, "days");
      } else if (
        selectedOption === "Last 60 days" &&
        newDate instanceof dayjs
      ) {
        calculatedStartDate = newDate.subtract(60, "days");
      }
      setStartDate(calculatedStartDate);
    };
    calculateUpdatedStartDate();
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRadio(e.target.value);
    setClickedLabel(null);
  };

  // Get overview data with proxy
  useEffect(() => {
    const selectedOptionInt = parseInt(selectedOption.replace(/\D/g, ""), 10);

    // Update the numbersAsInt state
    setNumbersAsInt(selectedOptionInt || 0);

    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    const formattedCallCenter = selectedCallCenter;

    Promise.all([
      fetch(
        `/total_call_volume?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${formattedCallCenter}`
      ),
      fetch(
        `/average_handle_time?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${formattedCallCenter}`
      ),
      fetch(
        `/resolution_rate?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${formattedCallCenter}`
      ),
      fetch(
        `/average_wait_time?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${formattedCallCenter}`
      ),
      fetch(
        `/total_unresolved?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${formattedCallCenter}`
      ),
      fetch(
        `/average_sentiment?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${formattedCallCenter}`
      ),
    ])
      .then(
        ([
          resTotalCall,
          resAveHandleTime,
          resResolRate,
          resAveWaitTime,
          resTotalUnresolved,
          resAveSentiment,
        ]) =>
          Promise.all([
            resTotalCall.json(),
            resAveHandleTime.json(),
            resResolRate.json(),
            resAveWaitTime.json(),
            resTotalUnresolved.json(),
            resAveSentiment.json(),
          ])
      )
      .then(
        ([
          dataTotalCall,
          dataAveHandleTime,
          dataResolRate,
          dataAveWaitTime,
          dataTotalUnresolved,
          dataAveSentiment,
        ]) => {
          setTotalCall(dataTotalCall);
          setAveHandleTime(dataAveHandleTime);
          setResolRate(dataResolRate);
          setAveWaittime(dataAveWaitTime);
          setTotalUnresolved(dataTotalUnresolved);
          setAveSentiment(dataAveSentiment);
        }
      );
  }, [selectedOption, selectedCallCenter, startDate, endDate]);

  // Get alerts data via proxy
  useEffect(() => {
    setIsLoading(true);
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    const fetchData = async () => {
      try {
        const response = await fetch(
          `/opm_view_llm_alerts?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center=${selectedCallCenter}`
        );
        const result = await response.json();
        //console.log(result)
        setAlertData(result);
        setAlertText([
          result.key_alerts[0].response,
          result.key_alerts[1].response,
        ]);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedOption, selectedCallCenter, startDate, endDate]);

  useEffect(() => {
    setIsHighlightLoading(true);
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    const url = `/llm_highlights?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center=${selectedCallCenter}`;

    const fetchHighlightsData = async (url: string) => {
      try {
        const response = await fetch(url);
        const result = await response.json();
        setHighlightData(result);
        setHighlightText([
          result.key_highlights.response[0].replace(/^\d+\.\s/, ""),
          result.key_highlights.response[1].replace(/^\d+\.\s/, ""),
          result.key_highlights.response[2].replace(/^\d+\.\s/, ""),
        ]);
        setIsHighlightLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsHighlightLoading(false);
      }
    };

    fetchHighlightsData(url);
  }, [selectedOption, startDate, endDate, selectedCallCenter]);

  const handleSendReport = () => {
    //console.log('Sending report:', { email, comments });
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openImage = () => {
    setIsImageOpen(true);
  };

  const closeImage = () => {
    setIsImageOpen(false);
  };

  const handleReset = () => {
    // Access the passed functions and reset state as needed
    setIsBeingResetTable(true);
    setIsBeingResetChart(true);
    setSelectedRadio("resolution_rate");
    setTotalCall([]);
    setAveHandleTime([]);
    setResolRate([]);
    setAveWaittime([]);
    setAveSentiment([]);
    setTotalUnresolved([]);
    setNumbersAsInt(0);
    setSelectedOption("Last 7 days");
    setEndDate(dayjs("2024-01-31"));
    setStartDate(dayjs("2024-01-24"));
    setAlertData("");
    setIsLoading(true);
    setClickedLabel(null);
    setSelectedCallCenter("CC001");
    setCallCenterText("Davis-Morgan");
    setSelectedReasonFromChild("");
    setSelectedSubReasonFromChild("");
  };

  return (
    <FadeIn transitionDuration={700}>
      <div className='ops-root container'>
        <FadeIn transitionDuration={700}>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FadeIn transitionDuration={700}>
                  <Item className='ops-navbar-filters'>
                    <Box sx={{ flexGrow: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={6} md={3} lg={1.5} xl={1.5}>
                          <FormControl fullWidth>
                            <InputLabel id='select-label'>
                              Reference Period
                            </InputLabel>
                            <Select
                              labelId='select-label'
                              id='select'
                              value={selectedOption}
                              onChange={(e) => {
                                setSelectedOption(e.target.value);
                                handleEndDateChange(e.target.value, endDate);
                              }}
                            >
                              <MenuItem value='Last 7 days'>
                                Last 7 days
                              </MenuItem>
                              <MenuItem value='Last 30 days'>
                                Last 30 days
                              </MenuItem>
                              <MenuItem value='Last 60 days'>
                                Last 60 days
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={6} md={3} lg={1.5} xl={1.5}>
                          <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale='en'
                          >
                            <div>
                              <DatePicker
                                defaultValue={dayjs("2024-01-31")}
                                label='Report Date'
                                value={endDate}
                                onChange={(e) => {
                                  handleEndDateChange(
                                    selectedOption,
                                    e || dayjs("2024-01-31")
                                  );
                                }}
                              />
                            </div>
                          </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={12} md={2} lg={1.5} xl={1.5}>
                          <FormControl fullWidth>
                            <InputLabel id='select-call-center'>
                              Call Center
                            </InputLabel>
                            <Select
                              labelId='select-cc-label'
                              id='select-cc'
                              value={selectedCallCenter}
                              onChange={(e) => {
                                setSelectedCallCenter(e.target.value);
                                handleCallCenterChange(
                                  e.target.value,
                                  selectedCallCenter
                                );
                              }}
                            >
                              <MenuItem value='CC001'>
                                Davis-Morgan Center
                              </MenuItem>
                              <MenuItem value='CC002'>
                                Dixon, Johnson and Dickerson Center
                              </MenuItem>
                              <MenuItem value='CC003'>
                                Davis-Ross Center
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={5} lg={4} xl={4}>
                          <div className='exec-buttons'>
                            <Button
                              className='ops-nav-btn exec-nav-btn'
                              variant='contained'
                              endIcon={<RestartAltIcon />}
                              onClick={handleReset}
                            >
                              Reset Filters
                            </Button>
                            <Button
                              className='ops-nav-btn exec-nav-btn'
                              variant='contained'
                              endIcon={<SendIcon />}
                              onClick={() => {
                                onButtonClick();
                                openModal();
                              }}
                            >
                              Send Report
                            </Button>
                          </div>
                        </Grid>
                        <Modal
                          className='ops-send-modal modal-global'
                          open={isModalOpen}
                          onClose={closeModal}
                          aria-labelledby='modal-modal-title'
                          aria-describedby='modal-modal-description'
                        >
                          <Box className='modal-style' sx={ModalStyle}>
                            <h3>
                              <SendIcon /> Report Form
                            </h3>
                            <div>
                              <TextField
                                label='Subject'
                                variant='outlined'
                                fullWidth
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                margin='normal'
                              />
                              <TextField
                                label='Email'
                                variant='outlined'
                                fullWidth
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                margin='normal'
                              />
                              <TextField
                                label='Additional Comments'
                                variant='outlined'
                                fullWidth
                                multiline
                                rows={4}
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                margin='normal'
                              />
                              <FormControl
                                fullWidth
                                className='send-report-dropdown'
                              >
                                <InputLabel id='select-label'>
                                  Report Frequency
                                </InputLabel>
                                <Select
                                  labelId='select-label'
                                  id='select'
                                  value={frequency}
                                  onChange={(e) => {
                                    setFrequency(e.target.value);
                                  }}
                                >
                                  <MenuItem value='Daily'>Daily</MenuItem>
                                  <MenuItem value='Weekly'>Weekly</MenuItem>
                                  <MenuItem value='Monthly'>Monthly</MenuItem>
                                </Select>
                              </FormControl>
                              <TextField
                                label='Num of Recurrences'
                                variant='outlined'
                                fullWidth
                                type='number'
                                value={recurrences}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value);
                                  if (
                                    !isNaN(value) &&
                                    value >= 0 &&
                                    value <= 365
                                  ) {
                                    setRecurrences(value.toString());
                                  } else if (e.target.value === "") {
                                    setRecurrences("");
                                  }
                                }}
                                margin='normal'
                                inputProps={{ inputMode: "numeric", max: 365 }}
                              />
                              <p className='text-dark-mode'>
                                Recurrence Range:
                              </p>
                              <LocalizationProvider
                                dateAdapter={AdapterDayjs}
                                adapterLocale='en'
                              >
                                <div className='send-report-datepickers'>
                                  <DatePicker
                                    className='exec-date-picker'
                                    label='Start'
                                    value={rangeStartDate}
                                    onChange={(date) => {
                                      if (date && date.isBefore(Today, "day")) {
                                        setRangeStartDate(Today);
                                      } else {
                                        setRangeStartDate(date || Today);
                                      }
                                    }}
                                  />
                                  <span className='text-dark-mode'> to </span>
                                  <DatePicker
                                    className='exec-date-picker'
                                    label='End'
                                    value={rangeEndDate}
                                    onChange={(date) => {
                                      if (date && date.isBefore(Today, "day")) {
                                        setRangeEndDate(Today);
                                      } else if (
                                        rangeStartDate &&
                                        date &&
                                        date.isBefore(rangeStartDate, "day")
                                      ) {
                                        setRangeEndDate(rangeStartDate);
                                      } else {
                                        setRangeEndDate(date || Today);
                                      }
                                    }}
                                  />
                                </div>
                              </LocalizationProvider>
                              {screenshot && (
                                <div className='send-report-attachment'>
                                  <p>Attachment:</p>
                                  <img
                                    src={screenshot}
                                    alt='Screenshot'
                                    onClick={openImage}
                                  />
                                </div>
                              )}
                            </div>
                            <div>
                              <Button
                                className='ops-send-btn'
                                variant='contained'
                                onClick={handleSendReport}
                              >
                                Send
                              </Button>
                            </div>
                          </Box>
                        </Modal>
                        <Modal
                          className='exec-send-modal modal-global'
                          open={isImageOpen}
                          onClose={closeImage}
                          aria-labelledby='modal-modal-title'
                          aria-describedby='modal-modal-description'
                        >
                          <Box sx={ImageStyle}>
                            <div className='magnified-content'>
                              <img
                                src={screenshot}
                                alt='Magnified Screenshot'
                              />
                            </div>
                          </Box>
                        </Modal>
                      </Grid>
                    </Box>
                  </Item>
                </FadeIn>
              </Grid>
              <Grid item xs={12} className='exec-alert-wrapper'>
                <Item className='ops-navbar'>
                  <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2}>
                      {isLoading ||
                      alertData.llm_response === "" ||
                      alertData.length === 0 ? (
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          lg={6}
                          xl={6}
                          className='exec-alert'
                        >
                          <Skeleton height={70} />
                          <span className='centered-text'>
                            <GridLoading
                              visible={true}
                              height='15'
                              width='15'
                              color={mode === "dark" ? "white" : "black"}
                              ariaLabel='grid-loading'
                              radius='12.5'
                              wrapperStyle={{}}
                              wrapperClass='grid-wrapper'
                            />
                            AI is analyzing key alerts...
                          </span>
                        </Grid>
                      ) : (
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          lg={6}
                          xl={6}
                          className={`exec-alert ${
                            clickedLabel === 0 ? "clicked" : "not-clicked"
                          }`}
                          onClick={() => handleLabelClick(0)}
                        >
                          <div
                            className='alert alert--info'
                            data-tooltip-id='ops-tooltip'
                          >
                            {mode === "dark" ? (
                              <AutoAwesomeIcon />
                            ) : (
                              <span className='ai-driven-light'>
                                <AutoAwesomeIcon />
                              </span>
                            )}
                            <div className='content'>
                              <div className='body'>
                                {/* <span>{alertText[0]}</span> */}
                                <TypeAnimation
                                  sequence={[`${alertText[0]}`]}
                                  speed={{
                                    type: "keyStrokeDelayInMs",
                                    value: 10,
                                  }}
                                  cursor={false}
                                />
                              </div>
                            </div>
                          </div>
                        </Grid>
                      )}
                      {isLoading ||
                      alertData.llm_response === "" ||
                      alertData.length === 0 ? (
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          lg={6}
                          xl={6}
                          className='exec-alert'
                        >
                          <Skeleton height={70} />
                          <span className='centered-text'>
                            <GridLoading
                              visible={true}
                              height='15'
                              width='15'
                              color={mode === "dark" ? "white" : "black"}
                              ariaLabel='grid-loading'
                              radius='12.5'
                              wrapperStyle={{}}
                              wrapperClass='grid-wrapper'
                            />
                            AI is analyzing key alerts...
                          </span>
                        </Grid>
                      ) : (
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          lg={6}
                          xl={6}
                          className={`exec-alert ${
                            clickedLabel === 1 ? "clicked" : "not-clicked"
                          }`}
                          onClick={() => handleLabelClick(1)}
                        >
                          <div
                            className='alert alert--info'
                            data-tooltip-id='ops-tooltip'
                          >
                            {mode === "dark" ? (
                              <AutoAwesomeIcon />
                            ) : (
                              <span className='ai-driven-light'>
                                <AutoAwesomeIcon />
                              </span>
                            )}
                            <div className='content'>
                              <div className='body'>
                                {/* <span>{alertText[1]}</span> */}
                                <TypeAnimation
                                  sequence={[`${alertText[1]}`]}
                                  speed={{
                                    type: "keyStrokeDelayInMs",
                                    value: 10,
                                  }}
                                  cursor={false}
                                />
                              </div>
                            </div>
                          </div>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Item>
              </Grid>
            </Grid>

            <Grid className='ops-container-wrapper' container spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={4.5} xl={4.5}>
                <BoxContainer className='ops-left-container'>
                  <FadeIn transitionDuration={700}>
                    <FormControl>
                      <RadioGroup
                        aria-labelledby='demo-radio-buttons-group-label'
                        defaultValue='total_call_volume'
                        name='radio-buttons-group'
                        value={selectedRadio ?? " "}
                        onChange={handleRadioChange}
                      >
                        <h3>{callCenterText} Resolution Metrics</h3>
                        <div className='ops-group'>
                          <div className='ops-group-box'>
                            <FormControlLabel
                              value='resolution_rate'
                              control={<Radio />}
                              label='Resolution Rate​'
                            />
                            {typeof resolRateData.resolution_rate ===
                            "undefined" ? (
                              <Skeleton width='100%' height='100px' />
                            ) : (
                              <div className='exec-radio-values'>
                                <div className='exec-sidebar-number'>
                                  {
                                    <span className='slot'>
                                      <SlotCounter
                                        value={parsePercentageString(
                                          resolRateData.resolution_rate
                                        )}
                                      />
                                    </span>
                                  }
                                  <span className='percentage-sign'>%</span>
                                  {mode === "dark" ? (
                                    <span className='shine'>
                                      <AutoAwesomeIcon />
                                    </span>
                                  ) : (
                                    <span className='ai-driven'>
                                      <AutoAwesomeIcon />
                                    </span>
                                  )}
                                </div>
                                {numbersAsInt !== 0 ? (
                                  <>
                                    {selectedOption !== "Last 7 days" ||
                                    resolRateData.percentage_change ===
                                      "N/A" ? (
                                      <span className='bold-num'>DATA N/A</span>
                                    ) : (
                                      <span
                                        className={`exec-sidebar-percentage`}
                                      >
                                        <span
                                          className={`${
                                            parseFloat(
                                              resolRateData.percentage_change
                                            ) === 0
                                              ? "no-arrow"
                                              : parseFloat(
                                                  resolRateData.percentage_change
                                                ) < 0
                                              ? "red-arrow"
                                              : "green-arrow"
                                          } number`}
                                        >
                                          {parseFloat(
                                            resolRateData.percentage_change
                                          ) === 0
                                            ? "00.0%"
                                            : (parseFloat(
                                                resolRateData.percentage_change
                                              ) < 0
                                                ? ""
                                                : "") +
                                              Math.abs(
                                                parseFloat(
                                                  resolRateData.percentage_change
                                                )
                                              ).toFixed(1) +
                                              "%"}
                                          {parseFloat(
                                            resolRateData.percentage_change
                                          ) === 0
                                            ? "⬅"
                                            : parseFloat(
                                                resolRateData.percentage_change
                                              ) < 0
                                            ? "⬇"
                                            : "⬆"}
                                        </span>
                                      </span>
                                    )}
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous {numbersAsInt} days
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    {selectedOption !== "Last 7 days" ||
                                    resolRateData.percentage_change ===
                                      "N/A" ? (
                                      <span className='bold-num'>DATA N/A</span>
                                    ) : (
                                      <span
                                        className={`exec-sidebar-percentage`}
                                      >
                                        <span
                                          className={`${
                                            parseFloat(
                                              resolRateData.percentage_change
                                            ) === 0
                                              ? "no-arrow"
                                              : parseFloat(
                                                  resolRateData.percentage_change
                                                ) < 0
                                              ? "red-arrow"
                                              : "green-arrow"
                                          } number`}
                                        >
                                          {parseFloat(
                                            resolRateData.percentage_change
                                          ) === 0
                                            ? "00.0%"
                                            : (parseFloat(
                                                resolRateData.percentage_change
                                              ) < 0
                                                ? ""
                                                : "") +
                                              Math.abs(
                                                parseFloat(
                                                  resolRateData.percentage_change
                                                )
                                              ).toFixed(1) +
                                              "%"}
                                          {parseFloat(
                                            resolRateData.percentage_change
                                          ) === 0
                                            ? "⬅"
                                            : parseFloat(
                                                resolRateData.percentage_change
                                              ) < 0
                                            ? "⬇"
                                            : "⬆"}
                                        </span>
                                      </span>
                                    )}
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous {numbersAsInt} days
                                    </span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <div className='ops-group-box'>
                            <FormControlLabel
                              value='average_sentiment'
                              control={<Radio />}
                              label='Average Sentiment​'
                            />
                            {typeof aveSentimentData.average_sentiment ===
                            "undefined" ? (
                              <Skeleton width='100%' height='100px' />
                            ) : (
                              <div className='exec-radio-values'>
                                <div className='exec-sidebar-number'>
                                  {
                                    <span className='slot'>
                                      <SlotCounter
                                        value={
                                          aveSentimentData.average_sentiment
                                        }
                                      />
                                    </span>
                                  }
                                  {mode === "dark" ? (
                                    <span className='shine'>
                                      <AutoAwesomeIcon />
                                    </span>
                                  ) : (
                                    <span className='ai-driven'>
                                      <AutoAwesomeIcon />
                                    </span>
                                  )}
                                </div>
                                {numbersAsInt !== 0 ? (
                                  <>
                                    {selectedOption !== "Last 7 days" ||
                                    aveSentimentData.percentage_change ===
                                      "N/A" ? (
                                      <span className='bold-num'>DATA N/A</span>
                                    ) : (
                                      <span
                                        className={`exec-sidebar-percentage`}
                                      >
                                        <span
                                          className={`${
                                            parseFloat(
                                              aveSentimentData.percentage_change
                                            ) === 0
                                              ? "no-arrow"
                                              : parseFloat(
                                                  aveSentimentData.percentage_change
                                                ) < 0
                                              ? "red-arrow"
                                              : "green-arrow"
                                          } number`}
                                        >
                                          {parseFloat(
                                            aveSentimentData.percentage_change
                                          ) === 0
                                            ? "00.0%"
                                            : (parseFloat(
                                                aveSentimentData.percentage_change
                                              ) < 0
                                                ? ""
                                                : "") +
                                              Math.abs(
                                                parseFloat(
                                                  aveSentimentData.percentage_change
                                                )
                                              ).toFixed(1) +
                                              "%"}
                                          {parseFloat(
                                            aveSentimentData.percentage_change
                                          ) === 0
                                            ? "⬅"
                                            : parseFloat(
                                                aveSentimentData.percentage_change
                                              ) < 0
                                            ? "⬇"
                                            : "⬆"}
                                        </span>
                                      </span>
                                    )}
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous {numbersAsInt} days
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    {selectedOption !== "Last 7 days" ||
                                    aveSentimentData.percentage_change ===
                                      "N/A" ? (
                                      <span className='bold-num'>DATA N/A</span>
                                    ) : (
                                      <span
                                        className={`exec-sidebar-percentage`}
                                      >
                                        <span
                                          className={`${
                                            parseFloat(
                                              aveSentimentData.percentage_change
                                            ) === 0
                                              ? "no-arrow"
                                              : parseFloat(
                                                  aveSentimentData.percentage_change
                                                ) < 0
                                              ? "red-arrow"
                                              : "green-arrow"
                                          } number`}
                                        >
                                          {parseFloat(
                                            aveSentimentData.percentage_change
                                          ) === 0
                                            ? "00.0%"
                                            : (parseFloat(
                                                aveSentimentData.percentage_change
                                              ) < 0
                                                ? ""
                                                : "") +
                                              Math.abs(
                                                parseFloat(
                                                  aveSentimentData.percentage_change
                                                )
                                              ).toFixed(1) +
                                              "%"}
                                          {parseFloat(
                                            aveSentimentData.percentage_change
                                          ) === 0
                                            ? "⬅"
                                            : parseFloat(
                                                aveSentimentData.percentage_change
                                              ) < 0
                                            ? "⬇"
                                            : "⬆"}
                                        </span>
                                      </span>
                                    )}
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous {numbersAsInt} days
                                    </span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <div className='ops-group-box'>
                            <FormControlLabel
                              value='unresolved_calls'
                              control={<Radio />}
                              label='Unresolved Calls​​​'
                            />
                            {typeof totalUnresolData.total_unresolved ===
                            "undefined" ? (
                              <Skeleton width='100%' height='100px' />
                            ) : (
                              <div className='exec-radio-values'>
                                <div className='exec-sidebar-number'>
                                  {
                                    <span className='slot'>
                                      <SlotCounter
                                        value={
                                          totalUnresolData.total_unresolved
                                        }
                                      />
                                    </span>
                                  }
                                  {mode === "dark" ? (
                                    <span className='shine'>
                                      <AutoAwesomeIcon />
                                    </span>
                                  ) : (
                                    <span className='ai-driven'>
                                      <AutoAwesomeIcon />
                                    </span>
                                  )}
                                </div>
                                {numbersAsInt !== 0 ? (
                                  <>
                                    {selectedOption !== "Last 7 days" ||
                                    totalUnresolData.percentage_change ===
                                      "N/A" ? (
                                      <span className='bold-num'>DATA N/A</span>
                                    ) : (
                                      <span
                                        className={`exec-sidebar-percentage`}
                                      >
                                        <span
                                          className={`${
                                            parseFloat(
                                              totalUnresolData.percentage_change
                                            ) === 0
                                              ? "no-arrow"
                                              : parseFloat(
                                                  totalUnresolData.percentage_change
                                                ) > 0
                                              ? "red-arrow"
                                              : "green-arrow"
                                          } number`}
                                        >
                                          {parseFloat(
                                            totalUnresolData.percentage_change
                                          ) === 0
                                            ? "00.0%"
                                            : (parseFloat(
                                                totalUnresolData.percentage_change
                                              ) < 0
                                                ? ""
                                                : "") +
                                              Math.abs(
                                                parseFloat(
                                                  totalUnresolData.percentage_change
                                                )
                                              ).toFixed(1) +
                                              "%"}
                                          {parseFloat(
                                            totalUnresolData.percentage_change
                                          ) === 0
                                            ? "⬅"
                                            : parseFloat(
                                                totalUnresolData.percentage_change
                                              ) < 0
                                            ? "⬇"
                                            : "⬆"}
                                        </span>
                                      </span>
                                    )}
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous {numbersAsInt} days
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    {selectedOption !== "Last 7 days" ||
                                    totalUnresolData.percentage_change ===
                                      "N/A" ? (
                                      <span className='bold-num'>DATA N/A</span>
                                    ) : (
                                      <span
                                        className={`exec-sidebar-percentage`}
                                      >
                                        <span
                                          className={`${
                                            parseFloat(
                                              totalUnresolData.percentage_change
                                            ) === 0
                                              ? "no-arrow"
                                              : parseFloat(
                                                  totalUnresolData.percentage_change
                                                ) > 0
                                              ? "red-arrow"
                                              : "green-arrow"
                                          } number`}
                                        >
                                          {parseFloat(
                                            totalUnresolData.percentage_change
                                          ) === 0
                                            ? "00.0%"
                                            : (parseFloat(
                                                totalUnresolData.percentage_change
                                              ) < 0
                                                ? ""
                                                : "") +
                                              Math.abs(
                                                parseFloat(
                                                  totalUnresolData.percentage_change
                                                )
                                              ).toFixed(1) +
                                              "%"}
                                          {parseFloat(
                                            totalUnresolData.percentage_change
                                          ) === 0
                                            ? "⬅"
                                            : parseFloat(
                                                totalUnresolData.percentage_change
                                              ) < 0
                                            ? "⬇"
                                            : "⬆"}
                                        </span>
                                      </span>
                                    )}
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous {numbersAsInt} days
                                    </span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <Divider className='ops-divider' />

                        <h3>{callCenterText} Major Inbound Call Metrics</h3>
                        <div className='ops-group'>
                          <div className='ops-group-box'>
                            <FormControlLabel
                              value='total_call_volume'
                              control={<Radio />}
                              label='Total Call Volume'
                            />
                            {typeof totalCallData.total_call_volume ===
                            "undefined" ? (
                              <Skeleton width='100%' height='100px' />
                            ) : (
                              <div className='exec-radio-values'>
                                <div className='exec-sidebar-number'>
                                  {
                                    <span className='slot'>
                                      <SlotCounter
                                        value={totalCallData.total_call_volume}
                                      />
                                    </span>
                                  }
                                </div>
                                {numbersAsInt !== 0 ? (
                                  <>
                                    {selectedOption !== "Last 7 days" ||
                                    totalCallData.percentage_change ===
                                      "N/A" ? (
                                      <span className='bold-num'>DATA N/A</span>
                                    ) : (
                                      <span
                                        className={`exec-sidebar-percentage`}
                                      >
                                        <span
                                          className={`${
                                            parseFloat(
                                              totalCallData.percentage_change
                                            ) === 0
                                              ? "no-arrow"
                                              : parseFloat(
                                                  totalCallData.percentage_change
                                                ) > 0
                                              ? "red-arrow"
                                              : "green-arrow"
                                          } number`}
                                        >
                                          {parseFloat(
                                            totalCallData.percentage_change
                                          ) === 0
                                            ? "00.0%"
                                            : (parseFloat(
                                                totalCallData.percentage_change
                                              ) < 0
                                                ? ""
                                                : "") +
                                              Math.abs(
                                                parseFloat(
                                                  totalCallData.percentage_change
                                                )
                                              ).toFixed(1) +
                                              "%"}
                                          {parseFloat(
                                            totalCallData.percentage_change
                                          ) === 0
                                            ? "⬅"
                                            : parseFloat(
                                                totalCallData.percentage_change
                                              ) < 0
                                            ? "⬇"
                                            : "⬆"}
                                        </span>
                                      </span>
                                    )}
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous {numbersAsInt} days
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    {selectedOption !== "Last 7 days" ||
                                    totalCallData.percentage_change ===
                                      "N/A" ? (
                                      <span className='bold-num'>DATA N/A</span>
                                    ) : (
                                      <span
                                        className={`exec-sidebar-percentage`}
                                      >
                                        <span
                                          className={`${
                                            parseFloat(
                                              totalCallData.percentage_change
                                            ) === 0
                                              ? "no-arrow"
                                              : parseFloat(
                                                  totalCallData.percentage_change
                                                ) < 0
                                              ? "red-arrow"
                                              : "green-arrow"
                                          } number`}
                                        >
                                          {parseFloat(
                                            totalCallData.percentage_change
                                          ) === 0
                                            ? "00.0%"
                                            : (parseFloat(
                                                totalCallData.percentage_change
                                              ) < 0
                                                ? ""
                                                : "") +
                                              Math.abs(
                                                parseFloat(
                                                  totalCallData.percentage_change
                                                )
                                              ).toFixed(1) +
                                              "%"}
                                          {parseFloat(
                                            totalCallData.percentage_change
                                          ) === 0
                                            ? "⬅"
                                            : parseFloat(
                                                totalCallData.percentage_change
                                              ) < 0
                                            ? "⬇"
                                            : "⬆"}
                                        </span>
                                      </span>
                                    )}
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous day
                                    </span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <div className='ops-group-box'>
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                              className='no-radio-label'
                            >
                              <span>Abandoned Calls in Queue</span>
                            </div>
                            <div className='exec-radio-values'>
                              <div className='exec-sidebar-number'>8</div>
                              <span className='exec-sidebar-percentage'>
                                <span className='red-arrow'>2% ⬆</span>
                              </span>
                              <br />
                              <span className='exec-sidebar-comment'>
                                vs. previous 7 days
                              </span>
                            </div>
                          </div>
                          <div className='ops-group-box'>
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                              className='no-radio-label'
                            >
                              <span>Abandoned Calls on Hold</span>
                            </div>
                            <div className='exec-radio-values'>
                              <div className='exec-sidebar-number'>5</div>
                              <span className='exec-sidebar-percentage'>
                                <span className='red-arrow'>2% ⬆</span>
                              </span>
                              <br />
                              <span className='exec-sidebar-comment'>
                                vs. previous 7 days
                              </span>
                            </div>
                          </div>
                        </div>
                        <Divider className='ops-divider' />

                        <h3>{callCenterText} Efficiency Metrics​</h3>
                        <div className='ops-group'>
                          <div className='ops-group-box'>
                            <FormControlLabel
                              value='average_handle_time'
                              control={<Radio />}
                              label='Average Handle Time​'
                            />
                            {typeof aveHandleData.average_handle_time ===
                            "undefined" ? (
                              <Skeleton width='100%' height='100px' />
                            ) : (
                              <div className='exec-radio-values'>
                                <div className='exec-sidebar-number'>
                                  {
                                    <span className='slot'>
                                      <SlotCounter
                                        value={Math.round(
                                          aveHandleData.average_handle_time
                                        )}
                                      />
                                    </span>
                                  }
                                  <span className='percentage-sign'>min</span>
                                </div>
                                {numbersAsInt !== 0 ? (
                                  <>
                                    {selectedOption !== "Last 7 days" ||
                                    aveHandleData.percentage_change ===
                                      "N/A" ? (
                                      <span className='bold-num'>DATA N/A</span>
                                    ) : (
                                      <span
                                        className={`exec-sidebar-percentage`}
                                      >
                                        <span
                                          className={`${
                                            parseFloat(
                                              aveHandleData.percentage_change
                                            ) === 0
                                              ? "no-arrow"
                                              : parseFloat(
                                                  aveHandleData.percentage_change
                                                ) > 0
                                              ? "red-arrow"
                                              : "green-arrow"
                                          } number`}
                                        >
                                          {parseFloat(
                                            aveHandleData.percentage_change
                                          ) === 0
                                            ? "00.0%"
                                            : (parseFloat(
                                                aveHandleData.percentage_change
                                              ) < 0
                                                ? ""
                                                : "") +
                                              Math.abs(
                                                parseFloat(
                                                  aveHandleData.percentage_change
                                                )
                                              ).toFixed(1) +
                                              "%"}
                                          {parseFloat(
                                            aveHandleData.percentage_change
                                          ) === 0
                                            ? "⬅"
                                            : parseFloat(
                                                aveHandleData.percentage_change
                                              ) < 0
                                            ? "⬇"
                                            : "⬆"}
                                        </span>
                                      </span>
                                    )}
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous {numbersAsInt} days
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    {selectedOption !== "Last 7 days" ||
                                    aveHandleData.percentage_change ===
                                      "N/A" ? (
                                      <span className='bold-num'>DATA N/A</span>
                                    ) : (
                                      <span
                                        className={`exec-sidebar-percentage`}
                                      >
                                        <span
                                          className={`${
                                            parseFloat(
                                              aveHandleData.percentage_change
                                            ) === 0
                                              ? "no-arrow"
                                              : parseFloat(
                                                  aveHandleData.percentage_change
                                                ) > 0
                                              ? "red-arrow"
                                              : "green-arrow"
                                          } number`}
                                        >
                                          {parseFloat(
                                            aveHandleData.percentage_change
                                          ) === 0
                                            ? "00.0%"
                                            : (parseFloat(
                                                aveHandleData.percentage_change
                                              ) < 0
                                                ? ""
                                                : "") +
                                              Math.abs(
                                                parseFloat(
                                                  aveHandleData.percentage_change
                                                )
                                              ).toFixed(1) +
                                              "%"}
                                          {parseFloat(
                                            aveHandleData.percentage_change
                                          ) === 0
                                            ? "⬅"
                                            : parseFloat(
                                                aveHandleData.percentage_change
                                              ) < 0
                                            ? "⬇"
                                            : "⬆"}
                                        </span>
                                      </span>
                                    )}
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous day
                                    </span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <div className='ops-group-box'>
                            <FormControlLabel
                              value='average_wait_time'
                              control={<Radio />}
                              label='Average Wait Time​'
                            />
                            {typeof aveWaitData.average_wait_time ===
                            "undefined" ? (
                              <Skeleton width='100%' height='100px' />
                            ) : (
                              <div className='exec-radio-values'>
                                <div className='exec-sidebar-number'>
                                  {
                                    <span className='slot'>
                                      <SlotCounter
                                        value={Math.round(
                                          aveWaitData.average_wait_time
                                        )}
                                      />
                                    </span>
                                  }
                                  <span className='percentage-sign'>sec</span>
                                </div>
                                {numbersAsInt !== 0 ? (
                                  <>
                                    {selectedOption !== "Last 7 days" ||
                                    aveWaitData.percentage_change === "N/A" ? (
                                      <span className='bold-num'>DATA N/A</span>
                                    ) : (
                                      <span
                                        className={`exec-sidebar-percentage`}
                                      >
                                        <span
                                          className={`${
                                            parseFloat(
                                              aveWaitData.percentage_change
                                            ) === 0
                                              ? "no-arrow"
                                              : parseFloat(
                                                  aveWaitData.percentage_change
                                                ) > 0
                                              ? "red-arrow"
                                              : "green-arrow"
                                          } number`}
                                        >
                                          {parseFloat(
                                            aveWaitData.percentage_change
                                          ) === 0
                                            ? "00.0%"
                                            : (parseFloat(
                                                aveWaitData.percentage_change
                                              ) < 0
                                                ? ""
                                                : "") +
                                              Math.abs(
                                                parseFloat(
                                                  aveWaitData.percentage_change
                                                )
                                              ).toFixed(1) +
                                              "%"}
                                          {parseFloat(
                                            aveWaitData.percentage_change
                                          ) === 0
                                            ? "⬅"
                                            : parseFloat(
                                                aveWaitData.percentage_change
                                              ) < 0
                                            ? "⬇"
                                            : "⬆"}
                                        </span>
                                      </span>
                                    )}
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous {numbersAsInt} days
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    {selectedOption !== "Last 7 days" ||
                                    aveWaitData.percentage_change === "N/A" ? (
                                      <span className='bold-num'>DATA N/A</span>
                                    ) : (
                                      <span
                                        className={`exec-sidebar-percentage`}
                                      >
                                        <span
                                          className={`${
                                            parseFloat(
                                              aveWaitData.percentage_change
                                            ) === 0
                                              ? "no-arrow"
                                              : parseFloat(
                                                  aveWaitData.percentage_change
                                                ) > 0
                                              ? "red-arrow"
                                              : "green-arrow"
                                          } number`}
                                        >
                                          {parseFloat(
                                            aveWaitData.percentage_change
                                          ) === 0
                                            ? "00.0%"
                                            : (parseFloat(
                                                aveWaitData.percentage_change
                                              ) < 0
                                                ? ""
                                                : "") +
                                              Math.abs(
                                                parseFloat(
                                                  aveWaitData.percentage_change
                                                )
                                              ).toFixed(1) +
                                              "%"}
                                          {parseFloat(
                                            aveWaitData.percentage_change
                                          ) === 0
                                            ? "⬅"
                                            : parseFloat(
                                                aveWaitData.percentage_change
                                              ) < 0
                                            ? "⬇"
                                            : "⬆"}
                                        </span>
                                      </span>
                                    )}
                                    <br />
                                    <span className='exec-sidebar-comment'>
                                      vs. previous day
                                    </span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <div className='ops-group-box'>
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                              className='no-radio-label'
                            >
                              <span>Average Answer Time</span>
                            </div>
                            <div className='exec-radio-values'>
                              <div className='exec-sidebar-number'>
                                15<span className='percentage-sign'>sec</span>
                              </div>
                              <span className='exec-sidebar-percentage'>
                                <span className='red-arrow'>2% ⬇</span>
                              </span>
                              <br />
                              <span className='exec-sidebar-comment'>
                                vs. previous 7 days
                              </span>
                            </div>
                          </div>
                        </div>
                      </RadioGroup>
                    </FormControl>
                  </FadeIn>
                </BoxContainer>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={7.5} xl={7.5}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
                    <FadeIn transitionDuration={700}>
                      <React.StrictMode>
                        <CollapsibleTable
                          parentToChild={{
                            startDate,
                            endDate,
                            selectedCallCenter,
                            selectedRadio,
                            isBeingResetTable,
                            selectedOption,
                            mode,
                          }}
                          onResetTable={changeResetTable}
                          onSelectedReasonChange={handleSelectedReasonChange}
                          onSelectedSubReasonChange={
                            handleSelectedSubReasonChange
                          }
                        />
                      </React.StrictMode>
                    </FadeIn>
                  </Grid>

                  <Grid
                    className='ops-paper-wrapper'
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    xl={6}
                  >
                    <BoxContainer className='ops-paper-container'>
                      <FadeIn transitionDuration={700}>
                        <OpsChart
                          parentToChild={{
                            startDate,
                            endDate,
                            selectedOption,
                            selectedRadio,
                            isBeingResetChart,
                            selectedSubReasonFromChild,
                            selectedReasonFromChild,
                            selectedCallCenter,
                            mode,
                          }}
                          onResetChart={changeResetChart}
                        />
                      </FadeIn>
                    </BoxContainer>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <BoxContainer className='topics-identified'>
                    {isHighlightLoading ||
                    highlightData === undefined ||
                    highlightData.length === 0 ? (
                      <div className='position-reletive'>
                        <Skeleton height={150} />
                        <span className='centered-text'>
                          <GridLoading
                            visible={true}
                            height='15'
                            width='15'
                            color={mode === "dark" ? "white" : "black"}
                            ariaLabel='grid-loading'
                            radius='12.5'
                            wrapperStyle={{}}
                            wrapperClass='grid-wrapper'
                          />
                          AI is generating key highlights...
                        </span>
                      </div>
                    ) : (
                      <div className='exec-highlight'>
                        <h3>
                          Key Highlights
                          {mode === "dark" ? (
                            <span className='shine'>
                              <AutoAwesomeIcon />
                            </span>
                          ) : (
                            <span className='ai-driven'>
                              <AutoAwesomeIcon />
                            </span>
                          )}
                        </h3>
                        <ul>
                          <li className='flex-list'>
                            <ArrowRightIcon />
                            <TypeAnimation
                              sequence={[`${highlightText[0]}`]}
                              speed={{ type: "keyStrokeDelayInMs", value: 10 }}
                              cursor={false}
                            />
                          </li>
                          <li className='flex-list'>
                            <ArrowRightIcon />
                            <TypeAnimation
                              sequence={[`${highlightText[1]}`]}
                              speed={{ type: "keyStrokeDelayInMs", value: 10 }}
                              cursor={false}
                            />
                          </li>
                          <li className='flex-list'>
                            <ArrowRightIcon />
                            <TypeAnimation
                              sequence={[`${highlightText[2]}`]}
                              speed={{ type: "keyStrokeDelayInMs", value: 10 }}
                              cursor={false}
                            />
                          </li>
                        </ul>
                      </div>
                    )}
                  </BoxContainer>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Item className='ops-card ops-search-section'>
                  <FadeIn transitionDuration={700}>
                    <div className='ops-search-header'>
                      <h3>Transcript Semantic Search</h3>
                    </div>
                    <React.StrictMode>
                      <Search
                        parentToChild={{
                          startDate,
                          endDate,
                          selectedOption,
                          mode,
                        }}
                      />
                    </React.StrictMode>
                  </FadeIn>
                </Item>
              </Grid>
            </Grid>
          </Box>
        </FadeIn>
      </div>
      <ReactTooltip
        id='ops-tooltip'
        style={{
          borderRadius: "5px",
          ...(mode === "dark" && {
            backgroundColor: "#f5f5f5",
            color: "black",
          }),
        }}
        place='bottom'
        content='Select to filter dashboard'
      />
    </FadeIn>
  );
}

export default OpsManager;
